import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../../components/ui/Badge.jsx'
import Button from '../../../components/ui/Button.jsx'
import { Field, Input } from '../../../components/ui/Field.jsx'
import { useFeedback } from '../../../components/ui/feedback.jsx'
import api from '../../../lib/api.js'
import { isValidIban, cleanIban, formatIban } from '../../../lib/validation.js'

/* Step 3 — bank account (required, ≥1). Reuses the bank-account create flow. */
export default function StepBank({ companyId, bindSubmit }) {
  const { t } = useTranslation()
  const { notify } = useFeedback()
  const [accounts, setAccounts] = useState([])
  const [f, setF] = useState({ account_name: '', bank_name: '', account_holder: '', iban: '', bic: '', is_default: true })
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const ibanInvalid = f.iban && !isValidIban(f.iban)
  const formFilled = f.account_name && f.iban && f.bic

  const load = useCallback(() => api.get(`/companies/${companyId}/bank-accounts`).then(({ data }) => setAccounts(data?.bank_accounts || [])), [companyId])
  useEffect(() => { load() }, [load])

  useEffect(() => {
    bindSubmit(async () => {
      // Create the typed account if any, then require at least one.
      if (formFilled) {
        if (!isValidIban(f.iban)) { notify({ tone: 'error', title: t('settings.error'), message: t('settings.bank.ibanInvalid') }); return false }
        try { await api.post(`/companies/${companyId}/bank-accounts`, { ...f, iban: cleanIban(f.iban), is_default: accounts.length === 0 || f.is_default }); await load() }
        catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }); return false }
        setF({ account_name: '', bank_name: '', account_holder: '', iban: '', bic: '', is_default: false })
        return true
      }
      if (accounts.length === 0) { notify({ tone: 'error', title: t('settings.error'), message: t('setup.steps.bank.required') }); return false }
      return true
    })
  }, [f, formFilled, accounts, companyId, bindSubmit, load, notify, t])

  return (
    <>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 16 }}>{t('setup.steps.bank.intro')}</p>
      {accounts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
          {accounts.map((a) => (
            <div key={a.id} style={{ padding: '11px 14px', border: '1px solid var(--color-border)', borderRadius: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{a.account_name}{a.is_default && <span style={{ marginLeft: 8 }}><Badge tone="info">{t('parties.default')}</Badge></span>}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13 }}>{formatIban(a.iban)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ borderTop: accounts.length > 0 ? '1px solid var(--color-border-row)' : 'none', paddingTop: accounts.length > 0 ? 16 : 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>{t('setup.steps.bank.add')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label={t('settings.bank.accountName')}><Input value={f.account_name} onChange={(e) => set('account_name', e.target.value)} /></Field>
          <Field label={t('settings.bank.bankName')}><Input value={f.bank_name} onChange={(e) => set('bank_name', e.target.value)} /></Field>
        </div>
        <Field label="IBAN" error={ibanInvalid ? t('settings.bank.ibanInvalid') : undefined}><Input value={f.iban} onChange={(e) => set('iban', formatIban(e.target.value))} placeholder="BE68 5390 0754 7034" style={ibanInvalid ? { borderColor: 'var(--color-danger)' } : undefined} /></Field>
        <Field label="BIC"><Input value={f.bic} onChange={(e) => set('bic', e.target.value.toUpperCase())} maxLength={11} /></Field>
      </div>
    </>
  )
}
